import { useState, useMemo, CSSProperties, useEffect } from "react";
import { useAppSelector } from "../../state/store";
import { useNavigate, useLocation } from "react-router";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from "@mui/material";
import { Check } from "../../components/Check/Check";
import { Row } from "../../components/Row";
import { Selection } from "../../components/Inputs";
import { BQATabs } from "../../components/BQATabs/BQATabs";
import { DataTable } from "../../components/DataTable/DataTable";
import { TournamentLink } from "../../components/TournamentComponents/TournamentLink";
import { TournamentText } from "../../components/TournamentComponents/TournamentText";
import { TournamentTrivia } from "../../components/TournamentComponents/TournamentTrivia";
import { TournamentSidebar } from "../../components/TournamentComponents/TournamentSidebar";
import { EditTournamentEvent } from "./EditTournaments/EditEventForm";
import { TQuizDisplay } from "./TQuizDisplay";
import { EditTQuizForm } from "./EditTournaments/EditQuizForm";
import { useEffectAfterMount } from "../../hooks/useEffectAfterMount";
import {
  PreliminaryResultDetails,
  TEvent,
  Tournament,
  TQuiz,
  TQuizzerBox,
  TQuizzerStats,
  TQuizzerStats2,
  TRoster,
  TStats,
  TStatsDetails,
  TTeamStats2,
} from "../../types/tournamentTypes";
import { bqaBlue, bqaLightBlue } from "../../util/colors";
import { getDateRangeDisplay } from "../../util/dateUtils";
import { getRankString, scrollToId, shuffle } from "../../util/generalUtil";
import {
  getTeamNameDisplay,
  getTQuizBackground,
} from "../../util/tournamentUtils";
import { tournamentEditAbility } from "../../util/userUtil";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./Tournaments.scss";
import { Elo } from "../../util/elo";

interface Props {
  page: Tournament;
}

/**
 * All details specific to a tournament - rosters, stats, quizzes, etc.
 */
export function TournamentDetailDisplay(props: Props) {
  const tournament = props.page;
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTab =
    location.state?.tournamentPageTab !== undefined
      ? location.state.tournamentPageTab
      : "overview";
  const selectedTeamId = location.state?.tournamentSelectedTeam as
    | string
    | undefined;
  const setSelectedTeam = (teamId: string | undefined) => {
    navigate(location.pathname, {
      state: { ...location.state, tournamentSelectedTeam: teamId },
      replace: true,
    });
  };

  const setSelEvent = (tab: string) =>
    navigate(location.pathname, {
      state: {
        ...location.state,
        tournamentPageTab: tab,
        selectedQuiz: undefined,
      },
      replace: true,
    });

  const selectedEvent = useMemo(
    () => tournament.events.find((ev) => ev.eventName === selectedTab),
    [tournament.events, selectedTab]
  );

  // Helps out with lookups, and is neded for individual quizzes
  const allQuizzersMap = useMemo(() => {
    const result: Record<string, string> = {};
    Object.values(props.page.rosters || {}).forEach((team) => {
      Object.keys(team.quizzers).forEach((qId) => {
        result[qId] = team.quizzers[qId];
      });
    });
    return result;
  }, [props.page.rosters]);

  return (
    <div>
      <BQATabs
        value={selectedTab}
        onChange={setSelEvent}
        tabs={[
          {
            title: "Tournament Overview",
            key: "overview",
          },
          {
            title: !!(tournament.stats || tournament.statsV2)
              ? "Final Standings"
              : "Preliminary Results",
            key: "standings",
            visible:
              !!(tournament.stats || tournament.statsV2) ||
              !!tournament.preliminaryResults,
          },
          {
            title: "Headlines",
            key: "headlines",
            visible: !!tournament.headlines,
          },
          ...tournament.events.map((ev) => ({
            title: ev.eventName,
            key: ev.eventName,
          })),
          {
            title: "Statistics",
            key: "stats",
            visible: tournament.events.length > 0,
          },
        ]}
      />
      {selectedEvent && (
        // Event
        <EventDisplay
          tournament={tournament}
          event={selectedEvent}
          selectedTeam={selectedTeamId}
          onSelectTeam={setSelectedTeam}
          key={selectedEvent.eventName}
          quizzersMap={allQuizzersMap}
        />
      )}
      {!selectedEvent && selectedTab === "overview" && (
        // Tournament Overview
        <TournamentOverviewDisplay
          tournament={tournament}
          selectedTeamId={selectedTeamId}
          setSelectedTeam={setSelectedTeam}
        />
      )}
      {!selectedEvent && selectedTab === "standings" && (
        // Standings
        <TournamentStandingsDisplay
          tournament={tournament}
          selectedTeam={selectedTeamId}
          onSelectTeam={setSelectedTeam}
          quizzersMap={allQuizzersMap}
        />
      )}
      {!selectedEvent &&
        selectedTab === "headlines" &&
        tournament.headlines && (
          // Headlines
          <div>
            <TournamentText text={tournament.headlines} />
          </div>
        )}
      {!selectedEvent && selectedTab === "stats" && (
        // Stats
        <TournamentStatsTab
          tournament={tournament}
          quizzersMap={allQuizzersMap}
        />
      )}
    </div>
  );
}

const TournamentOverviewDisplay = (props: {
  tournament: Tournament;
  selectedTeamId: string | undefined;
  setSelectedTeam: (team: string | undefined) => void;
}) => {
  const { tournament } = props;
  const mainWidth = 330;

  return (
    <div>
      <Grid container spacing={2}>
        <TournamentSidebar
          page={tournament}
          breakpoints={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 3 }}
        />
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 9 }}>
          {tournament.rosters && !tournament.isIndividual && (
            <TeamListDisplay
              teams={tournament.rosters}
              width={mainWidth}
              selectedTeam={props.selectedTeamId}
              onSelectTeam={props.setSelectedTeam}
            />
          )}
        </Grid>
      </Grid>
      <TournamentTrivia page={tournament} />
    </div>
  );
};

const TeamListDisplay = (props: {
  teams: Record<string, TRoster>;
  selectedTeam: string | undefined;
  onSelectTeam: (team: string | undefined) => void;
  width: number;
}) => {
  const selectedTeam = useMemo(() => {
    if (!props.selectedTeam) return undefined;
    return props.teams[props.selectedTeam];
  }, [props.selectedTeam]);

  const [selectedMinistry, setSelectedMinistry] = useState<string>("");

  const teamIdList = useMemo(
    () =>
      Object.keys(props.teams).sort((t1, t2) =>
        props.teams[t1].teamName.localeCompare(props.teams[t2].teamName)
      ),
    [props.teams]
  );

  const ministries = useMemo(() => {
    const ministryList = new Set<string>();
    teamIdList.forEach((team) => {
      const thisMinistry = props.teams[team].ministry;
      if (thisMinistry) ministryList.add(thisMinistry);
    });
    return Array.from(ministryList).sort();
  }, [teamIdList, props.teams]);

  useEffectAfterMount(() => {
    if (selectedTeam) {
      scrollToId("selected-roster");
    }
  }, [selectedTeam]);
  return (
    <Row style={{ marginBottom: 10 }}>
      {teamIdList.length > 0 && (
        <div
          className="main-box"
          style={{ width: props.width, marginRight: 10 }}
        >
          <h2 style={{ marginTop: 0, marginBottom: 10 }}>Team Rosters</h2>
          {ministries.length > 0 && (
            <Selection
              caption="Ministry:"
              value={selectedMinistry}
              values={ministries}
              onChange={setSelectedMinistry}
              noneText="All"
              style={{ marginBottom: 10 }}
            />
          )}
          <Row>
            {teamIdList
              .filter((team) => {
                if (!selectedMinistry) return true;
                return props.teams[team].ministry === selectedMinistry;
              })
              .map((team) => (
                <div key={team} style={{ marginTop: 5, width: "50%" }}>
                  <button
                    className="link"
                    style={{ textAlign: "left" }}
                    onClick={() =>
                      props.onSelectTeam(
                        props.selectedTeam === team ? undefined : team
                      )
                    }
                  >
                    {`${props.teams[team].teamName}`}
                  </button>
                </div>
              ))}
          </Row>
        </div>
      )}
      {selectedTeam && (
        <div
          className="main-box"
          style={{ minWidth: 200 }}
          id="selected-roster"
        >
          <TournamentLink
            style={{ fontWeight: "bold", fontSize: 18 }}
            pageId={selectedTeam.teamId || selectedTeam.teamName}
            text={getTeamNameDisplay(
              selectedTeam.teamName,
              selectedTeam.ministry
            )}
          />
          {selectedTeam.coach && (
            <p>
              <span>{"Coach: "}</span>
              <TournamentLink pageId={selectedTeam.coach} />
            </p>
          )}
          {Object.keys(selectedTeam.quizzers)
            .sort((k1, k2) => {
              // DNF to the bottom
              if (selectedTeam.dnf) {
                if (
                  selectedTeam.dnf.includes(k1) &&
                  !selectedTeam.dnf.includes(k2)
                )
                  return 1;
                if (
                  !selectedTeam.dnf.includes(k1) &&
                  selectedTeam.dnf.includes(k2)
                )
                  return -1;
              }
              // Sort by quizzer name
              return selectedTeam.quizzers[k1].localeCompare(
                selectedTeam.quizzers[k2]
              );
            })
            .map((qKey) => (
              <p key={qKey}>
                <TournamentLink pageId={selectedTeam.quizzers[qKey]} />
                {selectedTeam.dnf?.includes(qKey) && (
                  <span style={{ fontSize: 12 }}>{" - Did not Finish"}</span>
                )}
              </p>
            ))}
        </div>
      )}
    </Row>
  );
};

/**
 * Displays final standings for the tournament
 */
const TournamentStandingsDisplay = (props: {
  tournament: Tournament;
  selectedTeam: string | undefined;
  onSelectTeam: (team: string | undefined) => void;
  quizzersMap: Record<string, string>;
}) => {
  const { tournament } = props;

  const stats = tournament.statsV2 || tournament.stats;
  const { preliminaryResults } = tournament;

  const teamNameMap = useMemo(() => {
    const result: Record<string, string> = {};
    if (tournament.rosters) {
      Object.values(tournament.rosters).forEach((roster) => {
        Object.keys(roster.quizzers).forEach((qId) => {
          result[qId] = roster.teamName;
        });
      });
    }
    return result;
  }, [tournament]);
  const teamPrize = useMemo(
    () => stats?.team.some((team) => !!team.prize),
    [stats]
  );
  const quizzerPrize = useMemo(
    () => stats?.quizzer.some((quizzer) => !!quizzer.prize),
    [stats]
  );
  const quizzerDisplay = useMemo(() => {
    if (!props.selectedTeam) return stats?.quizzer;
    const qStats = stats?.quizzer as any[]; // temporary for stats v1
    if (!qStats) return undefined;
    return qStats.reduce((list, quizzer, index) => {
      if (
        props.selectedTeam ===
        ("name" in quizzer ? quizzer.team : teamNameMap[quizzer.qId])
      ) {
        return list.concat({
          ...quizzer,
          place: index + 1,
        });
      }
      return list;
    }, [] as TStatsDetails[]);
  }, [props.selectedTeam, stats]);

  if (!stats && !preliminaryResults) return null;

  return (
    <div style={{ padding: 15 }}>
      {tournament.additionalData.statsNotes && (
        <TournamentText text={tournament.additionalData.statsNotes} />
      )}
      {preliminaryResults && (
        <Accordion defaultExpanded={!stats}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <h2>{preliminaryResults.title}</h2>
          </AccordionSummary>
          <AccordionDetails>
            <DataTable<PreliminaryResultDetails & { teamName: string }>
              data={Object.keys(preliminaryResults.teams).map((teamName) => ({
                teamName,
                ...preliminaryResults!.teams[teamName],
              }))}
              style={{ maxWidth: 1000 }}
              columns={[
                {
                  title: "#",
                  render: (_, index) => String(index + 1),
                  property: "num",
                },
                {
                  title: "Team Name",
                  render: (obj) =>
                    getTeamNameDisplay(
                      obj.teamName,
                      tournament.rosters?.[obj.teamName].ministry
                    ),
                  property: "teamName",
                  sortFunc: true,
                },
                {
                  title: "Record",
                  render: (obj) =>
                    obj.record
                      ? `${obj.record[0]}-${obj.record[1]}-${obj.record[2]}`
                      : "-",
                  property: "record",
                  visible: preliminaryResults.includeGranular,
                },
                {
                  title: "Points",
                  render: (obj) =>
                    obj.points !== undefined ? obj.points : "-",
                  property: "points",
                  visible: preliminaryResults.includeGranular,
                },
                {
                  title: "Errors",
                  render: (obj) =>
                    obj.errors !== undefined ? obj.errors : "-",
                  property: "errors",
                  visible: preliminaryResults.includeGranular,
                },
                ...preliminaryResults.data.map((field) => ({
                  title: field.title,
                  property: field.title,
                  render: (obj: PreliminaryResultDetails) =>
                    obj.data[field.title],
                  sortFunc: true as true,
                  visible: preliminaryResults.data.length > 1,
                })),
                {
                  title:
                    preliminaryResults.data.length > 1
                      ? "Total"
                      : "Bracket Points",
                  render: (obj) => obj.total,
                  property: "total",
                  sortFunc: true,
                },
              ]}
              defaultSort={{
                property: "total",
                direction: "desc",
              }}
            />
          </AccordionDetails>
        </Accordion>
      )}
      {stats && !tournament.isIndividual && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <h2>Team Placements</h2>
          </AccordionSummary>
          <AccordionDetails>
            <DataTable<TStats | TTeamStats2>
              data={stats.team}
              style={{ maxWidth: 800 }}
              getKey={(obj) => obj.name}
              columns={[
                {
                  property: "rank",
                  title: "Rank",
                  render: (_, index) => index + 1,
                },
                {
                  property: "name",
                  title: "Team Name",
                  render: (obj) =>
                    getTeamNameDisplay(
                      obj.name,
                      tournament.rosters?.[obj.name]?.ministry
                    ),
                },
                {
                  property: "points",
                  title: "Points",
                  render: (obj) => obj.points,
                },
                {
                  property: "errors",
                  title: "Errors",
                  render: (obj) => obj.errors,
                },
                {
                  property: "qCount",
                  title: "Quiz Count",
                  render: (obj) => obj.qCount,
                },
                {
                  property: "prize",
                  title: "Prize",
                  render: (obj) => (obj.prize ? `$${obj.prize}` : ""),
                  visible: teamPrize,
                },
              ]}
              onClickCell={(obj) =>
                props.onSelectTeam(
                  props.selectedTeam === obj.name ? "" : obj.name
                )
              }
              rowDisplay={(obj) => ({
                selected: props.selectedTeam === obj.name,
              })}
            />
          </AccordionDetails>
        </Accordion>
      )}
      {stats && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Row style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>
                {props.tournament.isIndividual
                  ? "Final Placements"
                  : "Individual Statistics"}
              </h2>
              {props.selectedTeam && (
                <>
                  &nbsp;
                  <span>{"-"}</span>
                  <button
                    className="link"
                    onClick={(e) => {
                      e.stopPropagation()
                      props.onSelectTeam("")
                    }}
                  >
                    Clear Team
                  </button>
                </>
              )}
            </Row>
          </AccordionSummary>
          <AccordionDetails>
            <DataTable<TQuizzerStats | TQuizzerStats2>
              data={quizzerDisplay}
              style={{ maxWidth: 1000 }}
              getKey={(obj) => ("name" in obj ? obj.name : obj.qId)}
              columns={[
                {
                  property: "rank",
                  title: "Rank",
                  render: (obj, index) => obj.place || index + 1,
                },
                {
                  property: "name",
                  title: "Name",
                  render: (obj) =>
                    "name" in obj ? obj.name : props.quizzersMap[obj.qId],
                },
                {
                  property: "team",
                  title: "Team Name",
                  render: (obj) =>
                    "team" in obj ? obj.team : teamNameMap[obj.qId],
                  visible: !props.tournament.isIndividual,
                },
                {
                  property: "points",
                  title: "Points",
                  render: (obj) => obj.points,
                },
                {
                  property: "errors",
                  title: "Errors",
                  render: (obj) => obj.errors,
                },
                {
                  property: "qCount",
                  title: "Quiz Count",
                  render: (obj) => obj.qCount,
                },
                {
                  property: "prize",
                  title: "Prize",
                  render: (obj) => (obj.prize ? `$${obj.prize}` : ""),
                  visible: quizzerPrize,
                },
              ]}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

const EventDisplay = (props: {
  tournament: Tournament;
  event: TEvent;
  selectedTeam: string | undefined;
  onSelectTeam: (team: string | undefined) => void;
  quizzersMap: Record<number, string>;
}) => {
  const { lightMode } = useAppSelector((state) => state.settings);
  const { event } = props;
  const [isEditingQuiz, setIsEditingQuiz] = useState<boolean>(false);
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const { user } = useAppSelector((state) => state.authentication);
  const location = useLocation();
  const navigate = useNavigate();
  const selQuiz = location.state?.selectedQuiz || "";
  const setSelectedQuiz = (quizKey: string, replace = false) =>
    navigate(location.pathname, {
      state: { ...location.state, selectedQuiz: quizKey },
      replace,
    });
  const [eventScrollPosition, setEventScrollPosition] = useState<{
    left: number;
    top: number;
  }>();
  useEffect(() => {
    if (!selQuiz && eventScrollPosition) {
      const el = document.getElementById("event-display");
      if (el) {
        el.scrollTo({ left: eventScrollPosition.left });
        window.scrollTo({ top: eventScrollPosition.top });
      }
    }
  }, [selQuiz]);

  const getRoundName = (index: number) => {
    return event.roundNames?.[String(index)] || `Round ${index}`;
  };

  // Find out how big the event is
  let maxRound = 0;
  let maxSite = 0;
  event.quizzes.forEach((quiz) => {
    if (quiz.round > maxRound) maxRound = quiz.round;
    if (quiz.site > maxSite) maxSite = quiz.site;
  });

  const teamNames = useMemo(() => {
    const rosters = Object.values(props.tournament.rosters || {});
    return rosters.map((team) => team.teamName).sort();
  }, [props.tournament]);

  const hasEditAccess = tournamentEditAbility(user, props.tournament.host);

  const parts = selQuiz.split("-");
  const round = Number.parseInt(parts[0]);
  const site = Number.parseInt(parts[1]);
  const thisQuiz = event.quizzes.find(
    (quiz) => quiz.round === round && quiz.site === site
  );

  if (isEditingEvent) {
    return (
      <EditTournamentEvent
        isCreate={false}
        onGoBack={() => setIsEditingEvent(false)}
        event={props.event}
        tournament={props.tournament}
      />
    );
  }

  if (isEditingQuiz) {
    return (
      <EditTQuizForm
        tournament={props.tournament}
        event={props.event}
        quiz={thisQuiz}
        purpose={thisQuiz ? "UPDATE" : "CREATE"}
        onGoBack={() => setIsEditingQuiz(false)}
        quizzerMap={props.quizzersMap}
        teamNames={teamNames}
      />
    );
  }

  if (thisQuiz) {
    return (
      <TQuizDisplay
        key={`${thisQuiz.round}-${thisQuiz.site}`}
        tournament={props.tournament}
        event={event}
        quiz={thisQuiz}
        getRoundName={getRoundName}
        maxRound={maxRound}
        maxSite={maxSite}
        onBack={(nextEvent) => {
          navigate(-1);
          setIsEditingQuiz(false);
        }}
        onEdit={() => setIsEditingQuiz(true)}
        setSelectedQuiz={(quiz) =>
          setSelectedQuiz(`${quiz.round}-${quiz.site}`, true)
        }
        hasEditAccess={hasEditAccess}
        quizzersMap={props.quizzersMap}
        selectedTeam={props.selectedTeam}
      />
    );
  }

  // Sizes padding is included in height and width, so the actual size is a little smaller
  const quizHeight = 86;
  const quizWidth = 260;
  const padding = 8;
  const halfPadding = padding / 2;
  const headerSize = 40;

  const getBlackLineDisplay = () => {
    if (!event.blackLine) return null;
    const lineThickness = 2;
    const commonStyles: CSSProperties = {
      position: "absolute",
      borderRadius: lineThickness,
    };
    const elements: React.ReactElement[] = [];
    event.blackLine.forEach((site, index, arr) => {
      // Horizontal Part
      if (site > 0)
        elements.push(
          <div
            key={`${index}-hor`}
            className="black-line-of-death"
            style={{
              ...commonStyles,
              top: 40 + site * quizHeight - halfPadding - lineThickness / 2,
              height: lineThickness,
              left: 40 + index * quizWidth - halfPadding,
              width: quizWidth + 2,
            }}
          />
        );

      // Vertical part, if needed between two rounds
      if (index > 0 && site !== arr[index - 1]) {
        const firstSite = site;
        let secondSite = arr[index - 1];
        if (secondSite === 0) {
          secondSite = maxSite;
        }
        elements.push(
          <div
            key={`${index}-ver`}
            className="black-line-of-death"
            style={{
              ...commonStyles,
              top:
                40 +
                Math.min(firstSite, secondSite) * quizHeight -
                halfPadding -
                lineThickness / 2,
              left: 40 + index * quizWidth - halfPadding,
              width: lineThickness,
              height: Math.abs(firstSite - secondSite) * quizHeight,
            }}
          />
        );
      }
    });
    return elements;
  };

  return (
    <div key={event.eventName}>
      <Grid container spacing={2}>
        <Grid style={{ marginTop: 4 }}>
          <span>{getDateRangeDisplay(event.date)}</span>
        </Grid>
        {teamNames.length > 0 && (
          <Grid>
            <Selection
              caption="Highlight:"
              spacing={false}
              value={props.selectedTeam}
              values={teamNames}
              onChange={props.onSelectTeam}
              noneText="No Team"
              placeholder="Team"
            />
          </Grid>
        )}
        {hasEditAccess && (
          <Grid style={{ marginTop: 4 }}>
            <button className="link" onClick={() => setIsEditingQuiz(true)}>
              Add Quiz
            </button>
            <button
              className="link"
              style={{ marginLeft: 5 }}
              onClick={() => setIsEditingEvent(true)}
            >
              Edit Event
            </button>
          </Grid>
        )}
      </Grid>
      {event.description && <TournamentText text={event.description} />}
      <div id="event-display" style={{ overflowX: "scroll" }}>
        <div
          style={{
            position: "relative",
            width: quizWidth * maxRound + headerSize,
            height: quizHeight * maxSite + headerSize,
            marginTop: 10,
          }}
        >
          {Array.from(Array(maxSite)).map((_, index) => (
            <div
              key={index}
              className="round-site-header"
              style={{
                top: quizHeight * index + headerSize,
                left: 0,
                width: headerSize - padding,
                height: quizHeight - padding,
              }}
              aria-label={`Site ${index + 1}`}
            >
              {index + 1}
            </div>
          ))}
          {Array.from(Array(maxRound)).map((_, index) => (
            <div
              key={index}
              className="round-site-header"
              style={{
                top: 0,
                left: quizWidth * index + headerSize,
                width: quizWidth - padding,
                height: headerSize - padding,
              }}
            >
              {getRoundName(index + 1)}
            </div>
          ))}
          {event.quizzes.map((quiz) => {
            return (
              <button
                onClick={() => {
                  setSelectedQuiz(`${quiz.round}-${quiz.site}`);
                  const el = document.getElementById("event-display");
                  if (el)
                    setEventScrollPosition({
                      left: el.scrollLeft,
                      top: window.scrollY,
                    });
                }}
                key={quiz.quizId}
                className="quiz-wrapper"
                style={{
                  height: quizHeight - padding,
                  width: quizWidth - padding,
                  top: (quiz.site - 1) * quizHeight + headerSize,
                  left: (quiz.round - 1) * quizWidth + headerSize,
                  backgroundColor: getTQuizBackground(quiz, lightMode),
                  borderRadius: 4,
                }}
              >
                {quiz.isIndividual && (
                  <>
                    {quiz.teams[0].quizzers
                      .slice(0, 2)
                      .map((quizzer, index) => (
                        <span key={quizzer.qId} className="team-row">
                          <span style={{ width: "10%" }}>{index + 1}</span>
                          <span style={{ width: "75%" }}>
                            {props.quizzersMap[quizzer.qId]}
                          </span>
                          <span style={{ width: "15%", fontWeight: "bold" }}>
                            {quizzer.score === undefined
                              ? quizzer.p
                              : quizzer.score}
                          </span>
                        </span>
                      ))}
                    {quiz.teams[0].quizzers.length > 2 && (
                      <span className="team-row">
                        {`(+${quiz.teams[0].quizzers.length - 2})`}
                      </span>
                    )}
                  </>
                )}
                {!quiz.isIndividual &&
                  [...quiz.teams]
                    .sort((t1, t2) => t1.index - t2.index)
                    .map((team) => (
                      <span key={team.teamName} className="team-row">
                        <span style={{ width: "13%" }}>
                          {getRankString(team.p)}
                        </span>
                        <span
                          style={{
                            width: "calc(65% - 4px)",
                            backgroundColor:
                              props.selectedTeam === team.teamName
                                ? lightMode
                                  ? bqaLightBlue
                                  : bqaBlue
                                : undefined,
                            marginRight: 4,
                            paddingLeft: 2,
                            borderRadius: 2,
                          }}
                        >
                          {`${team.seed !== undefined ? `(${team.seed}) ` : ""}${
                            team.teamName
                          }`}
                        </span>
                        <span style={{ width: "14%", fontWeight: "bold" }}>
                          {team.s}
                        </span>
                        <span style={{ width: "8%", textAlign: "right" }}>
                          {team.e}
                        </span>
                      </span>
                    ))}
              </button>
            );
          })}
          {getBlackLineDisplay()}
        </div>
      </div>
    </div>
  );
};

const TournamentStatsTab = (props: {
  tournament: Tournament;
  quizzersMap: Record<number, string>;
}) => {
  const [selectedTeam, setSelectedTeam] = useState<string>();
  const { user } = useAppSelector((state) => state.authentication);

  interface TQuizzerStats {
    qId: number;
    team: string;
    points: number;
    errors: number;
    quizCount: number;
    custom: number;
  }
  interface TTeamStats {
    team: string;
    points: number;
    errors: number;
    quizCount: number;
    record: [number, number, number];
    bracket: number;
    custom: number;
  }
  interface TByQuizStats {
    // Metadata
    event: string;
    round: number;
    site: number;

    // Quiz Info
    teams: { teamName: string; place: number; points: number }[];
    points: number;
    errors: number;

    // Team Only
    answering: number;

    // Individual stuff
    indyScore?: number;
    rank?: number;

    //Misc
    qm?: string;
    flag?: string;
  }

  const [perQuiz, setPerQuiz] = useState(false);
  const [includeIndividual, setIncludeIndividual] = useState(true);
  const [includeEvents, setIncludeEvents] = useState<Set<string>>(
    new Set(props.tournament.events.map((ev) => ev.eventName))
  );
  const [selectedQuizzer, setSelectedQuizzer] = useState(-1);

  const maxRounds = useMemo(() => {
    let max = 0;
    props.tournament.events.forEach((event) => {
      if (!includeEvents.has(event.eventName)) return;
      event.quizzes.forEach((quiz) => {
        if (quiz.round > max) max = quiz.round;
      });
    });
    return max;
  }, [props.tournament.events, includeEvents]);

  const [includeRounds, setIncludeRounds] = useState<Set<number>>(new Set());

  type StatsViews = "QUIZZER" | "TEAM" | "QUIZZER_QUIZZES" | "TEAM_QUIZZES";
  const [statsViewing, setStatsViewing] = useState<StatsViews>("QUIZZER");

  const hasIndividual = useMemo(() => {
    for (let i = 0; i < props.tournament.events.length; i++) {
      for (let j = 0; j < props.tournament.events[i].quizzes.length; j++) {
        if (props.tournament.events[i].quizzes[j].isIndividual) return true;
      }
    }
    return false;
  }, [props.tournament]);

  const indivCustomStat = props.tournament.additionalData.indivCustomStat;
  const indivCalc = useMemo(() => {
    if (!indivCustomStat) return () => 0;
    const parts = indivCustomStat.split(",");
    const partDetail = parts.map((calc) => {
      const bounds = calc.split("|");
      const part = bounds[0];
      const rawValue = part.substring(1, 4);

      const isNegative = part.startsWith("-");
      let multiplier = Number.parseInt(part.replace(/\D/g, ""));
      if (isNaN(multiplier)) multiplier = 1;

      const indivOnly = part.includes("I");
      const teamOnly = part.includes("T");

      let min = -99999;

      if (bounds[1]) {
        const bnd = Number.parseInt(bounds[1].substring(1));
        if (bounds[1].includes("n")) {
          min = bnd;
        }
      }

      return {
        rawValue,
        multiplier: multiplier * (isNegative ? -1 : 1),
        indivOnly,
        teamOnly,
        min,
      };
    });

    return (quiz: TQuiz, box: TQuizzerBox) => {
      return partDetail.reduce((sum, part) => {
        // Individual only or team only
        if (part.indivOnly && !quiz.isIndividual) return sum;
        if (part.teamOnly && quiz.isIndividual) return sum;

        let raw = 0;
        switch (part.rawValue) {
          case "pnt":
            // Points
            raw = box.p * 20 + (box.p === 5 && box.e === 0 ? 10 : 0);
            break;
          case "err":
            // Errors
            raw = box.e;
            break;
          case "era":
            // Error A (first)
            raw = box.e >= 1 ? 1 : 0;
            break;
          case "erb":
            // Second error
            raw = box.e >= 2 ? 1 : 0;
            break;
          case "erc":
            // Third error
            raw = box.e >= 3 ? 1 : 0;
            break;
          case "scr":
            // Score from indivudal quiz
            raw = box.score || 0;
            break;
          case "ste":
            // Site of quiz
            raw = quiz.site;
            break;
          case "rnd":
            // Round
            raw = quiz.round;
            break;
          case "con":
            // Constant, probably with a multiplier
            raw = 1;
        }
        let add = raw * part.multiplier;
        if (add < part.min) add = part.min;
        return sum + add;
      }, 0);
    };
  }, [indivCustomStat]);

  const [showElo, setShowElo] = useState(false);
  const [elo, setElo] = useState<Record<string, number>>({});

  const quizzesUsing = useMemo(() => {
    const result: { event: string; quiz: TQuiz }[] = [];
    props.tournament.events.forEach((event) => {
      if (!includeEvents.has(event.eventName)) return;
      event.quizzes.forEach((quiz) => {
        if (includeRounds.size > 0 && !includeRounds.has(quiz.round)) return;
        if (!includeIndividual && quiz.isIndividual) return;
        result.push({ event: event.eventName, quiz });
      });
    });
    return result;
  }, [props.tournament, includeEvents, includeRounds, includeIndividual]);

  const calculatedStats = useMemo(() => {
    const result = {
      quizzer: [] as TQuizzerStats[],
      team: [] as TTeamStats[],
      byQuiz: [] as TByQuizStats[],
    };

    const statsComare = (
      s1: TQuizzerStats | TTeamStats,
      s2: TQuizzerStats | TTeamStats,
      useCustom = false
    ): number => {
      const getValue = (
        stat: TQuizzerStats | TTeamStats,
        usingErrors: boolean = false
      ) => {
        let result = useCustom
          ? stat.custom
          : usingErrors
            ? stat.errors
            : stat.points;
        if (perQuiz) return result / stat.quizCount;
        return result;
      };
      let thisResult = getValue(s2) - getValue(s1);
      if (thisResult === 0) {
        return getValue(s1, true) - getValue(s2, true);
      }
      return thisResult;
    };

    if (statsViewing === "QUIZZER") {
      const quizzerResult: Record<number, TQuizzerStats> = {};
      quizzesUsing.forEach(({ quiz }) => {
        quiz.teams.forEach((team) => {
          team.quizzers.forEach((quizzer) => {
            if (!props.quizzersMap[quizzer.qId]) return;
            if (!quizzerResult[quizzer.qId]) {
              quizzerResult[quizzer.qId] = {
                qId: quizzer.qId,
                team: team.teamName,
                points: 0,
                errors: 0,
                quizCount: 0,
                custom: 0,
              };
            }
            quizzerResult[quizzer.qId].points += quizzer.p * 20;
            if (quizzer.p === 5 && quizzer.e === 0)
              quizzerResult[quizzer.qId].points += 10;
            quizzerResult[quizzer.qId].errors += quizzer.e;
            quizzerResult[quizzer.qId].quizCount += 1;
            if (indivCustomStat) {
              quizzerResult[quizzer.qId].custom += indivCalc(quiz, quizzer);
            }
          });
        });
      });
      result.quizzer = Object.values(quizzerResult).sort((q1, q2) =>
        statsComare(q1, q2, !!indivCustomStat)
      );
      if (selectedTeam)
        result.quizzer = result.quizzer.filter((q) => q.team === selectedTeam);
    }
    if (statsViewing === "TEAM") {
      const teamResult: Record<string, TTeamStats> = {};
      quizzesUsing.forEach(({ quiz }) => {
        if (quiz.isIndividual) return;
        quiz.teams.forEach((team) => {
          if (!teamResult[team.teamName]) {
            teamResult[team.teamName] = {
              team: team.teamName,
              points: 0,
              errors: 0,
              quizCount: 0,
              record: [0, 0, 0],
              bracket: 0,
              custom: 0,
            };
          }
          teamResult[team.teamName].points += team.s;
          teamResult[team.teamName].errors += team.e;
          teamResult[team.teamName].quizCount += 1;
          if (team.p <= 3) {
            teamResult[team.teamName].record[team.p - 1] += 1;
            teamResult[team.teamName].bracket +=
              2 + (3 - team.p) * 4 + 0.01 * team.s;
          }
        });
      });
      result.team = Object.values(teamResult).sort(statsComare);
    }
    if (statsViewing === "QUIZZER_QUIZZES" || statsViewing === "TEAM_QUIZZES") {
      props.tournament.events.forEach((ev) => {
        if (!includeEvents.has(ev.eventName)) return;
        ev.quizzes.forEach((quiz) => {
          if (includeRounds.size > 0 && !includeRounds.has(quiz.round)) return;
          if (!includeIndividual && quiz.isIndividual) return;
          if (quiz.isIndividual) {
            if (!quiz.teams[0].quizzers.some((q) => q.qId === selectedQuizzer))
              return;
          } else {
            if (!quiz.teams.some((team) => team.teamName === selectedTeam))
              return;
          }
          const thisQuiz: TByQuizStats = {
            event: ev.eventName,
            round: quiz.round,
            site: quiz.site,
            flag: quiz.flag,
            teams: [],
            errors: 0,
            points: 0,
            answering: 0,
            qm: quiz.qm,
          };
          const sortedTeams = [...quiz.teams].sort((t1, t2) => t1.p - t2.p);
          sortedTeams.forEach((team) => {
            thisQuiz.teams.push({
              teamName: team.teamName,
              place: team.p,
              points: team.s,
            });
            if (team.teamName === selectedTeam || quiz.isIndividual) {
              if (statsViewing === "QUIZZER_QUIZZES") {
                team.quizzers.forEach((q, i) => {
                  if (q.qId === selectedQuizzer) {
                    thisQuiz.points =
                      q.p * 20 + (q.p === 5 && q.e === 0 ? 10 : 0);
                    thisQuiz.errors = q.e;
                    if (quiz.isIndividual) {
                      thisQuiz.indyScore = q.score;
                      thisQuiz.rank = i + 1;
                    }
                  }
                });
                result.byQuiz.push(thisQuiz);
              } else {
                thisQuiz.points = team.s;
                thisQuiz.errors = team.e;
                thisQuiz.answering = team.quizzers.filter(
                  (q) => q.p > 0
                ).length;
                result.byQuiz.push(thisQuiz);
              }
            }
          });
        });
      });
    }

    return result;
  }, [
    includeEvents,
    includeRounds,
    props.tournament,
    statsViewing,
    perQuiz,
    includeIndividual,
    selectedTeam,
    selectedQuizzer,
  ]);

  const teamIdList = useMemo(
    () =>
      !props.tournament.rosters
        ? []
        : Object.keys(props.tournament.rosters).sort((t1, t2) =>
            props.tournament.rosters![t1].teamName.localeCompare(
              props.tournament.rosters![t2].teamName
            )
          ),
    [props.tournament.rosters]
  );
  const quizzerIdList = useMemo(() => {
    if (props.tournament.isIndividual) {
      let result: Record<string, string> = {};
      Object.values(props.tournament.rosters!).forEach((roster) => {
        result = {
          ...result,
          ...roster.quizzers,
        };
      });
      return result;
    } else {
      if (!selectedTeam) return {};
      const roster = props.tournament.rosters![selectedTeam];
      return roster.quizzers;
    }
  }, [selectedTeam, props.tournament.rosters, props.tournament.isIndividual]);

  const roundStats = (value: number, decimalPlaces = 2) =>
    Math.round(value * Math.pow(10, decimalPlaces)) /
    Math.pow(10, decimalPlaces);

  const getViewCheck = (key: StatsViews, title: string) => (
    <Grid>
      <Check
        checked={false}
        neverChecked
        outline={statsViewing === key}
        onClick={() => {
          setStatsViewing(key);
          setSelectedQuizzer(-1);
          setShowElo(false);
        }}
        style={{ width: 160 }}
      >
        {title}
      </Check>
    </Grid>
  );
  return (
    <div>
      <p>
        Use this tool to get <b>unofficial</b> stats from the quizzes included
        in this tournament. Note that scores from overtime will be included. For
        final placements, see the Final Standings tab
        {props.tournament.statsV2 || props.tournament.stats
          ? ""
          : ", once final standings are posted"}
        .
      </p>
      <Grid container spacing={1}>
        {getViewCheck("QUIZZER", "Quizzer")}
        {!props.tournament.isIndividual && getViewCheck("TEAM", "Team")}
        {getViewCheck("QUIZZER_QUIZZES", "Quizzer By Quiz")}
        {!props.tournament.isIndividual &&
          getViewCheck("TEAM_QUIZZES", "Team By Quiz")}
        {user?.securityClearance.logonType === "ADMIN" &&
          !showElo &&
          (statsViewing === "QUIZZER" || statsViewing === "TEAM") && (
            <Grid>
              <button
                className="secondary-btn"
                onClick={() => {
                  const eloCalc = new Elo()
                  Object.values(props.tournament.rosters || {}).forEach(
                    (team) => {
                      if (statsViewing === "QUIZZER") {
                        Object.keys(team.quizzers).forEach((qId) => {
                          eloCalc.addPlayer(qId)
                        });
                      } else {
                        eloCalc.addPlayer(team.teamName)
                      }
                    }
                  );
                  const quizList: { quiz: TQuiz; event: string }[] = [];
                  const iterations = 50
                  for (let i = 0; i < iterations; i++) {
                    const qAdd = shuffle([...quizzesUsing]);
                    quizList.push(...qAdd);
                  }
                  quizList.forEach(({ quiz }, index) => {
                    if (statsViewing === "TEAM") {
                      quiz.teams.forEach((t1) => {
                        quiz.teams.forEach((t2) => {
                          if (t1 === t2) return;
                          eloCalc.compete(t1.teamName, t2.teamName, t1.p < t2.p ? 1 : 0)
                        });
                      });
                    } else {
                      const quizzerScore = (q: TQuizzerBox) =>
                        q.score || (q.p * 20 - q.e * 13 - q.f * 6 - q.t * 10);
                      const ratingsThisQuiz: [string, string, number][] = [];
                      quiz.teams.forEach((t1) => {
                        t1.quizzers.forEach((q1) => {
                          quiz.teams.forEach((t2) => {
                            t2.quizzers.forEach((q2) => {
                              if (q1 === q2) return;
                              const q1Score = quizzerScore(q1);
                              const q2Score = quizzerScore(q2);

                              ratingsThisQuiz.push([
                                String(q1.qId),
                                String(q2.qId),
                                q1Score > q2Score
                                  ? 1
                                  : q1Score < q2Score
                                    ? 0
                                    : 0.5,
                              ]);
                            });
                          });
                        });
                      });
                      shuffle(ratingsThisQuiz).forEach((rating) => {
                        eloCalc.compete(rating[0], rating[1], rating[2])
                      })
                    }

                    if (index % iterations === 0) eloCalc.lowerFactor()
                  });

                  setElo(eloCalc.players);
                  setShowElo(true);
                }}
              >
                Calculate Elo
              </button>
            </Grid>
          )}
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 10 }}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
          <h5 style={{ margin: 0 }}>Include quizzes from these events:</h5>
          <Grid container spacing={1}>
            {props.tournament.events.map((ev) => (
              <Grid
                key={ev.eventName}
                size={{ xs: 6, sm: 4, md: 3, lg: 4, xl: 4 }}
              >
                <Check
                  style={{ height: 32 }}
                  checked={false}
                  neverChecked
                  outline={includeEvents.has(ev.eventName)}
                  onClick={() => {
                    const newInclude = new Set(includeEvents);
                    if (includeEvents.has(ev.eventName)) {
                      newInclude.delete(ev.eventName);
                    } else {
                      newInclude.add(ev.eventName);
                    }
                    setIncludeEvents(newInclude);
                    setIncludeRounds(new Set());
                    setShowElo(false);
                  }}
                >
                  {ev.eventName}
                </Check>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
          <h5 style={{ margin: 0 }}>Include quizzes from these rounds:</h5>
          <Row style={{ marginBottom: 10 }}>
            {new Array(maxRounds).fill(0).map((_, round) => (
              <Check
                key={round}
                style={{ width: 40, height: 32 }}
                checked={false}
                neverChecked
                outline={includeRounds.has(round + 1)}
                onClick={() => {
                  const newInclude = new Set(includeRounds);
                  if (includeRounds.has(round + 1)) {
                    newInclude.delete(round + 1);
                  } else {
                    newInclude.add(round + 1);
                  }
                  setIncludeRounds(newInclude);
                  setShowElo(false);
                }}
              >
                {String(round + 1)}
              </Check>
            ))}
          </Row>
        </Grid>
      </Grid>
      <Grid style={{ marginTop: 10, marginBottom: 10 }} container spacing={1}>
        {(statsViewing === "QUIZZER" || statsViewing === "TEAM") && (
          <Grid>
            <Check
              style={{ width: 170 }}
              checked={false}
              outline={perQuiz}
              onClick={() => setPerQuiz(!perQuiz)}
            >
              Show Per Quiz
            </Check>
          </Grid>
        )}

        {hasIndividual && !props.tournament.isIndividual && (
          <Grid>
            <Check
              style={{ width: 220 }}
              checked={false}
              outline={includeIndividual}
              onClick={() => {
                setIncludeIndividual(!includeIndividual);
                setShowElo(false);
              }}
            >
              Include Individual Quizzes
            </Check>
          </Grid>
        )}
        {statsViewing !== "TEAM" && !props.tournament.isIndividual && (
          <Grid>
            <Selection
              caption={`${
                statsViewing === "QUIZZER" ? "Filter by" : "Select a"
              } Team:`}
              value={selectedTeam}
              onChange={(team) => {
                setSelectedTeam(team);
                setSelectedQuizzer(-1);
              }}
              values={teamIdList}
              spacing={false}
              noneText={statsViewing === "QUIZZER" ? "All" : undefined}
              placeholder="Select a Team"
              style={{ marginTop: 6 }}
            />
          </Grid>
        )}
        {statsViewing === "QUIZZER_QUIZZES" &&
          (selectedTeam || props.tournament.isIndividual) && (
            <Grid>
              <Selection
                caption="Select a Quizzer:"
                value={selectedQuizzer >= 0 ? String(selectedQuizzer) : ""}
                onChange={(value) => setSelectedQuizzer(Number.parseInt(value))}
                values={quizzerIdList}
                sortOptions={(q1, _, q2) => q1.localeCompare(q2)}
                spacing={false}
                placeholder="Select a Quizzer"
                style={{ marginTop: 6 }}
              />
            </Grid>
          )}
      </Grid>
      {statsViewing === "QUIZZER" && (
        <DataTable<TQuizzerStats>
          columns={[
            {
              title: "Place",
              property: "place",
              render: (_, index) => index + 1,
            },
            {
              title: "Name",
              property: "name",
              render: (obj) => props.quizzersMap[obj.qId],
            },
            {
              title: "Team",
              property: "team",
              render: (obj) => obj.team,
              visible: !props.tournament.isIndividual,
            },
            {
              title: perQuiz ? "Points/Q" : "Points",
              property: "points",
              render: (obj) =>
                perQuiz ? roundStats(obj.points / obj.quizCount) : obj.points,
              sortFunc: true,
            },
            {
              title: perQuiz ? "Errors/Q" : "Errors",
              property: "errors",
              render: (obj) =>
                perQuiz ? roundStats(obj.errors / obj.quizCount) : obj.errors,
              sortFunc: true,
            },
            {
              title: "Quiz Count",
              property: "quizCount",
              render: (obj) => obj.quizCount,
              sortFunc: true,
            },
            {
              title:
                props.tournament.additionalData.indivCustomStatName ||
                "Custom Stat",
              property: "custom",
              render: (obj) =>
                perQuiz ? roundStats(obj.custom / obj.quizCount) : obj.custom,
              sortFunc: true,
              visible: !!indivCustomStat,
            },
            {
              title: "Elo",
              property: "elo",
              render: (obj) => Math.round(elo[obj.qId]),
              visible: showElo,
              sortFunc: (obj) => elo[obj.qId],
              defaultSortDirection: "desc",
            },
          ]}
          data={calculatedStats.quizzer}
          getKey={(obj) => obj.qId}
          defaultSort={{
            property: indivCustomStat ? "custom" : showElo ? "elo" : "points",
            direction: "desc",
          }}
        />
      )}
      {statsViewing === "TEAM" && (
        <DataTable<TTeamStats>
          columns={[
            {
              title: "Place",
              property: "place",
              render: (_, index) => index + 1,
            },
            {
              title: "Team",
              property: "team",
              render: (obj) =>
                `${obj.team}${
                  props.tournament.rosters![obj.team]?.ministry
                    ? ` - ${props.tournament.rosters![obj.team].ministry}`
                    : ""
                }`,
            },
            {
              title: "Record",
              property: "record",
              render: (obj) =>
                `${obj.record[0]}-${obj.record[1]}-${obj.record[2]}`,
              sortFunc: (obj) => obj.record[0],
            },
            {
              title: perQuiz ? "Points/Q" : "Points",
              property: "points",
              render: (obj) =>
                perQuiz ? roundStats(obj.points / obj.quizCount) : obj.points,
              sortFunc: true,
            },
            {
              title: perQuiz ? "Errors/Q" : "Errors",
              property: "errors",
              render: (obj) =>
                perQuiz ? roundStats(obj.errors / obj.quizCount) : obj.errors,
              sortFunc: true,
            },
            {
              title: perQuiz ? "Bracket/Q" : "Bracket Points",
              property: "bracket",
              render: (obj) =>
                roundStats(
                  perQuiz ? obj.bracket / obj.quizCount : obj.bracket,
                  1
                ),
              sortFunc: true,
            },
            {
              title: "Quiz Count",
              property: "quizCount",
              render: (obj) => obj.quizCount,
              sortFunc: true,
            },
            {
              title: "Elo",
              property: "elo",
              render: (obj) => Math.round(elo[obj.team]),
              visible: showElo,
              sortFunc: (obj) => elo[obj.team],
              defaultSortDirection: "desc",
            },
          ]}
          getKey={(obj) => obj.team}
          data={calculatedStats.team}
          defaultSort={{
            property: showElo ? "elo" : "bracket",
            direction: "desc",
          }}
        />
      )}
      {(statsViewing === "QUIZZER_QUIZZES" ||
        statsViewing === "TEAM_QUIZZES") &&
        !!(
          props.tournament.isIndividual ||
          selectedQuizzer >= 0 ||
          statsViewing === "TEAM_QUIZZES"
        ) && (
          <DataTable<TByQuizStats>
            columns={[
              {
                title: "Quiz",
                property: "quiz",
                render: (obj) =>
                  `${obj.event} R${obj.round} S${obj.site}${
                    obj.flag ? ` (${obj.flag})` : ""
                  }`,
                sortFunc: (obj) =>
                  props.tournament.events.findIndex(
                    (ev) => ev.eventName === obj.event
                  ) *
                    10000 +
                  obj.round * 100 +
                  obj.site,
              },
              {
                title: "Points",
                property: "points",
                render: (obj) => <b>{obj.points}</b>,
                sortFunc: (obj) => obj.points,
                defaultSortDirection: "desc",
              },
              {
                title: "Errors",
                property: "errors",
                render: (obj) => obj.errors,
                sortFunc: true,
                defaultSortDirection: "desc",
              },
              {
                title: "Answering",
                property: "answering",
                render: (obj) => obj.answering,
                sortFunc: true,
                defaultSortDirection: "desc",
                visible: statsViewing === "TEAM_QUIZZES",
              },
              {
                title: "Score",
                property: "score",
                render: (obj) => obj.indyScore,
                visible:
                  hasIndividual &&
                  includeIndividual &&
                  statsViewing === "QUIZZER_QUIZZES",
                sortFunc: true,
                defaultSortDirection: "desc",
              },
              {
                title: "Rank",
                property: "rank",
                render: (obj) => obj.rank,
                visible:
                  hasIndividual &&
                  includeIndividual &&
                  statsViewing === "QUIZZER_QUIZZES",
                sortFunc: true,
                defaultSortDirection: "asc",
              },
              ...["1st", "2nd", "3rd"].map((title, index) => ({
                title,
                property: `t${index}`,
                render: (obj: TByQuizStats) => {
                  const team = obj.teams[index];
                  if (!team?.teamName) return "";
                  return (
                    <span
                      style={{
                        fontWeight:
                          team.teamName === selectedTeam ? "bold" : undefined,
                        display: "block",
                      }}
                    >
                      <span
                        className="no-wrap-text"
                        style={{ maxWidth: "calc(100% - 20px)" }}
                      >
                        {`${team.teamName}: `}
                      </span>
                      <span style={{ minWidth: 20 }}>{team.points}</span>
                    </span>
                  );
                },
                visible: !props.tournament.isIndividual,
              })),
              {
                title: "QM",
                property: "qm",
                render: (obj) => obj.qm,
                sortFunc: true,
              },
            ]}
            getKey={(obj) => `${obj.event}-${obj.round}-${obj.site}`}
            data={calculatedStats.byQuiz}
            defaultSort={{
              property: "quiz",
              direction: "asc",
            }}
          />
        )}
    </div>
  );
};
